import * as React from "react";

import logger from "../../../log";
import { stopRun } from "../../Experiences/core/runs";
import useAppWorkoutPlay from "../../Trainer/AppWorkouts/useAppWorkoutPlay";
import { useCurrentUser } from "../../hooks/currentUser";
import { usePairingContext } from "../../hooks/pairingStatus";
import { useStatus } from "../../hooks/status";
import { useAppWorkoutStatus } from "../../hooks/useAppWorkoutStatus";
import LowerHeadDialog from "../LowerHeadDialog";
import { shouldSolicitFeedback } from "../SessionFeedbackDialog";
import UnpairDialog from "../UnpairDialog";
import useDialog from "../useDialog";

const enum UnpairSteps {
    StopWorkout,
    LowerArm,
    Unpair,
    SolicitFeedback,
    CleanUp,
    Done,
}

export default function NoConfirmUnpairFlow(): React.JSX.Element | null {
    const { unpair } = usePairingContext();
    const { setDialogType } = useDialog();
    const { isAdmin } = useCurrentUser();
    const { status } = useStatus();
    const { currentWorkout, playState } = useAppWorkoutStatus();
    const { stop: stopWorkout } = useAppWorkoutPlay({
        workout: null,
        parameters: {},
    });
    const [step, setStep] = React.useState(UnpairSteps.StopWorkout);

    React.useEffect(() => {
        if (step === UnpairSteps.CleanUp) {
            setDialogType("DisconnectCleanupChecklist");
        }
    }, [step, setDialogType, unpair]);

    React.useEffect(() => {
        const handleStopWorkout = async () => {
            if (step === UnpairSteps.StopWorkout) {
                if (currentWorkout && playState !== "stopped") {
                    try {
                        await stopWorkout();
                    } catch (error) {
                        logger.error("Failed to stop workout:", error as Error);
                    }
                } else {
                    // An experience run is not a legacy workout, and the
                    // trainer does not stop one when the session closes.
                    // "Nothing running" is the usual answer, and a trainer
                    // without experiences refuses — neither holds this up.
                    if (status?.clientId !== undefined) {
                        await stopRun(status.clientId).catch(() => undefined);
                    }
                    setStep(UnpairSteps.LowerArm);
                }
            }
        };

        handleStopWorkout();
    }, [step, currentWorkout, playState, stopWorkout, status?.clientId]);

    React.useEffect(() => {
        if (step === UnpairSteps.StopWorkout && playState === "stopped") {
            setStep(UnpairSteps.LowerArm);
        }
    }, [step, playState]);

    if (step === UnpairSteps.LowerArm) {
        return (
            <LowerHeadDialog
                message="The trainer arm is moving to the lowest position for storage."
                reason="unpair"
                onLowered={() => setStep(UnpairSteps.Unpair)}
                onStopped={() => setDialogType(null)}
                onError={() => setStep(UnpairSteps.Unpair)} // if we can't lower, continue with shutdown
            />
        );
    }

    if (step === UnpairSteps.Unpair) {
        return (
            <UnpairDialog
                onUnpair={() => {
                    unpair();
                    setStep(UnpairSteps.SolicitFeedback);
                }}
            />
        );
    }

    if (step === UnpairSteps.SolicitFeedback) {
        // don't prompt for feedback if the user is an admin
        if (isAdmin()) {
            setStep(UnpairSteps.CleanUp);
        }

        // if we shouldn't prompt for feedback, continue with to cleanup
        if (!shouldSolicitFeedback(status?.session)) {
            setStep(UnpairSteps.CleanUp);
        }

        setDialogType("DisconnectFeedback");
    }

    return null;
}
